import React, { useCallback, useState } from 'react';
import Table, { TableHeader, TableRow, TableCell, TableBody } from '@/components/ui/Table';
import cn from '@/utils/cn';
import { flexRender, Table as ReactTable } from '@tanstack/react-table';
import {
  IAnalyticRubricCriterion,
} from '@/components/staffer/ExerciseBuilder/Rubric/useQuestionRubric';
import DeleteCriterionButton
  from '@/components/staffer/ExerciseBuilder/Rubric/AddRubricModal/AnalyticRubric/DeleteCriterionButton';
import { isEmptyContent } from '../AddRubricModal.utils';
import useCellValidation from '../hooks/useCellValidation';
import styles from './AnalyticRubric.module.scss';

const CELL_CLASSES = 'tw-relative tw-border-solid tw-border-[1px] tw-border-med-grey tw-text-left';
const HIGHLIGHT_CLASSES = 'tw-border-2 tw-border-red';

export type AnalyticRubricProps = {
  classes?: {
    cell?: string,
    container?: string,
    criterion_column?: string,
    header?: string,
  };
  hasHeaders?: boolean;
  table: ReactTable<IAnalyticRubricCriterion>;
  validationAttempt?: number;
  editor?: boolean;
  locale?: Lowercase<Locale>;
  onDeleteCriteria?: (criterionIndex: number) => void;
};

const AnalyticRubric = ({
  table,
  classes = {},
  hasHeaders = true,
  validationAttempt = 0,
  editor = false,
  locale = 'en',
  onDeleteCriteria,
}: AnalyticRubricProps) => {
  const { acknowledgedCells, handleCellClick, getCellKey } = useCellValidation({ validationAttempt });
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  const handleCellHover = (rowIndex: number | null) => {
    if (!editor) return;
    setHoveredRowIndex(rowIndex);
  };

  const isCellEmpty = useCallback((rowData: IAnalyticRubricCriterion, columnId: string) => {
    if (columnId === 'criterion') {
      return !rowData.name_en || isEmptyContent(rowData.name_en);
    }

    if (columnId.startsWith('level_')) {
      const score = parseInt(columnId.replace('level_', ''), 10);
      const level = rowData.levels.find(l => l.score === score);

      return !level?.description_en || isEmptyContent(level.description_en);
    }

    return false;
  }, []);

  const shouldHighlightCell = (rowData: IAnalyticRubricCriterion, columnId: string, rowIndex: number) => {
    if (validationAttempt === 0) return false;

    const cellKey = getCellKey(rowIndex, columnId);
    const isAcknowledged = acknowledgedCells.has(cellKey);
    const isEmpty = isCellEmpty(rowData, columnId);

    return isEmpty && !isAcknowledged;
  };

  const totalCriteria = table.getRowModel().rows.length;

  return (
    <Table className={cn('tw-w-full', classes.container)}>
      {hasHeaders && (
      <TableHeader className="tw-bg-light-grey tw-h-[3.75rem]">
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <TableCell
                key={header.id}
                header
                className={cn(CELL_CLASSES, classes.header, 'tw-font-semibold tw-align-middle')}
                style={{ width: header.column.columnDef.meta?.width }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      )}
      <TableBody>
        {table.getRowModel().rows.map(row => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell, i) => {
              const isHighlighted = shouldHighlightCell(row.original, cell.column.id, row.index);

              return (
                <TableCell
                  key={cell.id}
                  className={cn(
                    styles.analyticDescriptionCell, // Specifically for Froala
                    CELL_CLASSES,
                    classes.cell,
                    isHighlighted && HIGHLIGHT_CLASSES,
                    i === 0 && !editor && classes.criterion_column,
                    i === 0 && editor && 'tw-group',
                    {
                      'tw-bg-light-grey': i === 0,
                      'tw-bg-white': i !== 0,
                    },
                  )}
                  style={{ width: cell.column.columnDef.meta?.width }}
                  onClick={() => handleCellClick(row.index, cell.column.id)}
                >
                  {i === 0 && editor ? (
                    <div
                      onMouseEnter={() => handleCellHover(row.index)}
                      onMouseLeave={() => handleCellHover(null)}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  ) : (
                    flexRender(cell.column.columnDef.cell, cell.getContext())
                  )}
                  {i === 0 && editor && (
                  <DeleteCriterionButton
                    cellFocused={hoveredRowIndex === row.index}
                    criterionData={row.original}
                    locale={locale}
                    totalCriteria={totalCriteria}
                    onDelete={() => onDeleteCriteria?.(row.index)}
                  />
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default AnalyticRubric;
